import {
  alpha,
  decomposeColor,
  type PaletteMode,
  type ThemeOptions,
} from '@mui/material/styles';

import {
  createThemeSettings,
  FALLBACK_DEFAULT_PARKDNA_DARK_THEME_PALETTE,
  FALLBACK_PARKDNA_DEFAULT_LIGHT_THEME_PALETTE,
  type ThemePalettes,
} from './theme-constants';
import tokens from './tokens.v2.json';

export type ThemeVersion = 'v1' | 'v2';

const defaultPalettes: ThemePalettes = {
  light: FALLBACK_PARKDNA_DEFAULT_LIGHT_THEME_PALETTE,
  dark: FALLBACK_DEFAULT_PARKDNA_DARK_THEME_PALETTE,
};

const hslToRgb = ([h, s, l]: number[]) => {
  const sat = s / 100;
  const light = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sat * Math.min(light, 1 - light);
  const f = (n: number) =>
    light - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [0, 8, 4].map((n) => Math.round(f(n) * 255));
};

/** Channels of any css color the palette may use (hex, rgb, hsl). */
export const toRgbChannels = (color: string): number[] => {
  const { type, values } = decomposeColor(color);
  return (type.startsWith('hsl') ? hslToRgb(values) : values).slice(0, 3);
};

const isSameColor = (a: string, b: string) =>
  toRgbChannels(a).every((v, i) => Math.abs(v - toRgbChannels(b)[i]) <= 2);

/**
 * v2 theme. Tokens that map to a palette key a white-label client can set
 * (primary, background, text.primary, error, action and the rest) are read from
 * the supplied palettes; tokens ParkDNA fixes (borders, muted text, delta,
 * inverse, chart neutrals, radius, type) come from tokens.v2.json. Values
 * derived from primary (soft tint, chart focus series, focus ring) follow it.
 */
export const createThemeSettingsV2 = (
  mode: PaletteMode,
  palettes: ThemePalettes = defaultPalettes
): ThemeOptions => {
  const c = tokens.color[mode];
  const chart = tokens.chart[mode];
  const base = createThemeSettings(mode, palettes);
  const clientPalette = palettes[mode];
  const primary = clientPalette.primary.main;
  const isDefaultPrimary = isSameColor(primary, c.primary);
  const primarySoft = isDefaultPrimary
    ? c.primarySoft
    : alpha(primary, mode === 'dark' ? 0.1 : 0.12);
  const focusRing = {
    outline: `${tokens.focus.ringWidth}px solid ${primary}`,
    outlineOffset: tokens.focus.ringOffset,
  };

  return {
    ...base,
    palette: {
      ...base.palette,
      // Keep MUI's computed contrast text for a client primary; the token
      // onPrimary is only locked for the default brand color.
      primary: {
        main: primary,
        soft: primarySoft,
        ...(isDefaultPrimary && { contrastText: c.onPrimary }),
      },
      divider: c.border,
      text: {
        primary: clientPalette.text.primary,
        // Captions and real content use muted (WCAG AA); subtle is decoration.
        secondary: c.textMuted,
        disabled: c.textSubtle,
        muted: c.textMuted,
        subtle: c.textSubtle,
      },
      surface: { muted: c.surfaceMuted },
      border: { default: c.border, strong: c.borderStrong },
      inverse: {
        main: c.inverse,
        contrastText: c.onInverse,
        muted: c.onInverseMuted,
      },
      delta: {
        positive: { fg: c.deltaPositiveFg, bg: c.deltaPositiveBg },
        negative: { fg: c.deltaNegativeFg, bg: c.deltaNegativeBg },
      },
    },
    typography: { ...base.typography, ...tokens.typography },
    chart: {
      series: [primary, ...chart.series.slice(1)],
      grid: chart.grid,
      track: c.surfaceMuted,
      axis: c.textMuted,
      tint: primarySoft,
      alert: clientPalette.error.main,
    },
    radius: tokens.radius,
    spacingTokens: tokens.spacing,
    iconSize: tokens.iconSize,
    focusRing: () => focusRing,
  };
};
